import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/contexts/AuthContext'
import { ToastProvider } from '@/contexts/ToastContext'
import ProtectedRoute from '@/components/ProtectedRoute'
import Layout from '@/components/Layout/Layout'
import LayoutSimple from '@/components/Layout/LayoutSimple'
import Loader from '@/components/UI/Loader'
import { ROUTES } from '@/utils/constants'
import { useAuth } from '@/hooks/useAuth'

const CashierDashboardGate = ({ children }) => {
  const { user } = useAuth()
  const roles = user?.roles || []
  if (roles.includes('ROLE_CASHIER')) {
    return <Navigate to="/caisse" replace />
  }
  return children
}

const RootRedirect = () => {
  const { user } = useAuth()
  const roles = user?.roles || []
  if (roles.includes('ROLE_CASHIER')) {
    return <Navigate to="/caisse" replace />
  }
  return <Navigate to={ROUTES.DASHBOARD} replace />
}

/** Anciens liens /bons-livraison/:id (ex. tickets caisse) → éditeur BL */
const LegacyBonLivraisonRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`${ROUTES.DELIVERY_NOTES}/${id}/edit`} replace />
}

// Lazy load pages for code splitting
const Login = lazy(() => import('@/pages/Auth/Login'))
// Inscription désactivée en production (comptes créés par admin)
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const DashboardSimple = lazy(() => import('@/pages/DashboardSimple'))
const ClientsList = lazy(() => import('@/pages/Clients/ClientsList'))
const ClientForm = lazy(() => import('@/pages/Clients/ClientForm'))
const ClientDetail = lazy(() => import('@/pages/Clients/ClientDetail'))
const ProduitsList = lazy(() => import('@/pages/Produits/ProduitsList'))
const ProduitForm = lazy(() => import('@/pages/Produits/ProduitForm'))
const ProduitDetail = lazy(() => import('@/pages/Produits/ProduitDetail'))
const ProductLabels = lazy(() => import('@/pages/Produits/ProductLabels'))
const FacturesList = lazy(() => import('@/pages/Factures/FacturesList'))
const FactureEditor = lazy(() => import('@/pages/Factures/FactureEditor'))
const FactureDetail = lazy(() => import('@/pages/Factures/FactureDetail'))
const AvoirsList = lazy(() => import('@/pages/Avoirs/AvoirsList'))
const AvoirEditor = lazy(() => import('@/pages/Avoirs/AvoirEditor'))
const QuotesList = lazy(() => import('@/pages/Quotes/QuotesList'))
const QuoteEditor = lazy(() => import('@/pages/Quotes/QuoteEditor'))
const QuoteDetail = lazy(() => import('@/pages/Quotes/QuoteDetail'))
const DeliveryNotesList = lazy(() => import('@/pages/Livraisons/DeliveryNotesList'))
const DeliveryNoteEditor = lazy(() => import('@/pages/Livraisons/DeliveryNoteEditor'))
const InvoiceFromDeliveryNotes = lazy(() => import('@/pages/Livraisons/InvoiceFromDeliveryNotes'))
const PaymentsList = lazy(() => import('@/pages/Payments/PaymentsList'))
const PaymentRegularization = lazy(() => import('@/pages/Payments/PaymentRegularization'))
const ClientGlobalBalance = lazy(() => import('@/pages/Payments/ClientGlobalBalance'))
const PaymentEditor = lazy(() => import('@/pages/Payments/PaymentEditor'))
const PaymentDetail = lazy(() => import('@/pages/Payments/PaymentDetail'))
const StockList = lazy(() => import('@/pages/Stock/StockList'))
const StockDashboard = lazy(() => import('@/pages/Stock/StockDashboard'))
const StockAnalytics = lazy(() => import('@/pages/Stock/StockAnalytics'))
const WarehousesList = lazy(() => import('@/pages/Warehouses/WarehousesList'))
const WarehouseForm = lazy(() => import('@/pages/Warehouses/WarehouseForm'))
const WarehouseTransfer = lazy(() => import('@/pages/Warehouses/TransferForm'))
const Reports = lazy(() => import('@/pages/Rapports/Reports'))
const Analytics = lazy(() => import('@/pages/Analytics/Analytics'))
const AchatsDashboard = lazy(() => import('@/pages/Achats/AchatsDashboard'))
const PurchaseRequestsList = lazy(() => import('@/pages/Achats/PurchaseRequestsList'))
const PurchaseRequestEditor = lazy(() => import('@/pages/Achats/PurchaseRequestEditor'))
const PurchaseOrdersList = lazy(() => import('@/pages/Achats/PurchaseOrdersList'))
const PurchaseOrderEditor = lazy(() => import('@/pages/Achats/PurchaseOrderEditor'))
const SuppliersList = lazy(() => import('@/pages/Achats/SuppliersList'))
const SupplierForm = lazy(() => import('@/pages/Achats/SupplierForm'))
const PurchaseInvoicesList = lazy(() => import('@/pages/Achats/PurchaseInvoicesList'))
const PurchaseInvoiceForm = lazy(() => import('@/pages/Achats/PurchaseInvoiceForm'))
const InvoiceFromGoodsReceipts = lazy(() => import('@/pages/Achats/InvoiceFromGoodsReceipts'))
const PurchasePaymentsList = lazy(() => import('@/pages/Achats/PurchasePaymentsList'))
const TejExportPage = lazy(() => import('@/pages/Achats/TejExportPage'))
const SupplierPaymentRegularization = lazy(() => import('@/pages/Achats/SupplierPaymentRegularization'))
const SupplierGlobalBalance = lazy(() => import('@/pages/Achats/SupplierGlobalBalance'))
const GoodsReceiptsList = lazy(() => import('@/pages/Achats/GoodsReceiptsList'))
const GoodsReceiptForm = lazy(() => import('@/pages/Achats/GoodsReceiptForm'))
const SupplierReturnsList = lazy(() => import('@/pages/Achats/SupplierReturnsList'))
const SupplierReturnForm = lazy(() => import('@/pages/Achats/SupplierReturnForm'))
const ChatIA = lazy(() => import('@/pages/ChatIA'))
const Profile = lazy(() => import('@/pages/Settings/Profile'))
const Settings = lazy(() => import('@/pages/Settings/Settings'))
const CashiersManagement = lazy(() => import('@/pages/Settings/CashiersManagement'))
const TestPage = lazy(() => import('@/pages/TestPage'))

// Caisse (POS)
const CaisseDashboard = lazy(() => import('@/pages/Caisse/CaisseDashboard'))
const POSInterface = lazy(() => import('@/pages/Caisse/POSInterface'))
const SessionManager = lazy(() => import('@/pages/Caisse/SessionManager'))
const CloseSession = lazy(() => import('@/pages/Caisse/CloseSession'))
const CaissePointsList = lazy(() => import('@/pages/Caisse/CaissePointsList'))
const SessionHistory = lazy(() => import('@/pages/Caisse/SessionHistory'))
const TicketHistory = lazy(() => import('@/pages/Caisse/TicketHistory'))
const CaisseMouvements = lazy(() => import('@/pages/Caisse/CaisseMouvements'))
const PayInvoiceFromCaisse = lazy(() => import('@/pages/Caisse/PayInvoiceFromCaisse'))
const CaisseReports = lazy(() => import('@/pages/Caisse/CaisseReports'))

// Super Admin
const UserManagement = lazy(() => import('@/pages/SuperAdmin/UserManagement'))
const EntrepriseManagement = lazy(() => import('@/pages/SuperAdmin/EntrepriseManagement'))
const RoleManagement = lazy(() => import('@/pages/SuperAdmin/RoleManagement'))

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Suspense fallback={<Loader fullScreen />}>
              <Routes>
                {/* Public routes */}
                <Route path={ROUTES.LOGIN} element={<Login />} />
                <Route path={ROUTES.REGISTER} element={<Navigate to={ROUTES.LOGIN} replace />} />

                {/* Protected routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path={ROUTES.DASHBOARD}
                    element={
                      <CashierDashboardGate>
                        <Dashboard />
                      </CashierDashboardGate>
                    }
                  />
                  
                  {/* Clients */}
                  <Route path={ROUTES.CLIENTS} element={<ClientsList />} />
                  <Route path={`${ROUTES.CLIENTS}/new`} element={<ClientForm />} />
                  <Route path={`${ROUTES.CLIENTS}/:id`} element={<ClientDetail />} />
                  <Route path={`${ROUTES.CLIENTS}/:id/edit`} element={<ClientForm />} />
                  
                  {/* Products */}
                  <Route path={ROUTES.PRODUITS} element={<ProduitsList />} />
                  <Route path={`${ROUTES.PRODUITS}/new`} element={<ProduitForm />} />
                  <Route path={`${ROUTES.PRODUITS}/:id`} element={<ProduitDetail />} />
                  <Route path={`${ROUTES.PRODUITS}/:id/edit`} element={<ProduitForm />} />
                  <Route path={ROUTES.PRODUCT_LABELS} element={<ProductLabels />} />
                  
                  {/* Invoices */}
                  <Route path={ROUTES.FACTURES} element={<FacturesList />} />
                  <Route path={`${ROUTES.FACTURES}/new`} element={<FactureEditor />} />
                  <Route path={`${ROUTES.FACTURES}/:id`} element={<FactureDetail />} />
                  <Route path={`${ROUTES.FACTURES}/:id/edit`} element={<FactureEditor />} />

                  {/* Avoirs */}
                  <Route path={ROUTES.AVOIRS} element={<AvoirsList />} />
                  <Route path={`${ROUTES.AVOIRS}/nouveau`} element={<AvoirEditor />} />
                  <Route path={`${ROUTES.AVOIRS}/:id`} element={<AvoirEditor />} />

                  {/* Delivery notes */}
                  <Route path={ROUTES.DELIVERY_NOTES_INVOICE} element={<InvoiceFromDeliveryNotes />} />
                  <Route path={`${ROUTES.DELIVERY_NOTES}/new`} element={<DeliveryNoteEditor />} />
                  <Route path={`${ROUTES.DELIVERY_NOTES}/:id/edit`} element={<DeliveryNoteEditor />} />
                  <Route path={ROUTES.DELIVERY_NOTES} element={<DeliveryNotesList />} />
                  <Route path="/bons-livraison/:id" element={<LegacyBonLivraisonRedirect />} />

                  {/* Quotes */}
                  <Route path={ROUTES.QUOTES} element={<QuotesList />} />
                  <Route path={`${ROUTES.QUOTES}/new`} element={<QuoteEditor />} />
                  <Route path={`${ROUTES.QUOTES}/:id`} element={<QuoteDetail />} />
                  <Route path={`${ROUTES.QUOTES}/:id/edit`} element={<QuoteEditor />} />
                  
                  {/* Payments */}
                  <Route path={ROUTES.PAYMENTS_REGULARIZATION_GLOBAL} element={<ClientGlobalBalance />} />
                  <Route path={ROUTES.PAYMENTS_REGULARIZATION} element={<PaymentRegularization />} />
                  <Route path={ROUTES.PAYMENTS} element={<PaymentsList />} />
                  <Route path={`${ROUTES.PAYMENTS}/new`} element={<PaymentEditor />} />
                  <Route path={`${ROUTES.PAYMENTS}/:id`} element={<PaymentDetail />} />
                  <Route path={`${ROUTES.PAYMENTS}/:id/edit`} element={<PaymentEditor />} />

                  {/* Purchases */}
                  <Route path={ROUTES.ACHATS_DASHBOARD} element={<AchatsDashboard />} />
                  <Route path={ROUTES.PURCHASE_REQUESTS} element={<PurchaseRequestsList />} />
                  <Route path={`${ROUTES.PURCHASE_REQUESTS}/new`} element={<PurchaseRequestEditor />} />
                  <Route path={`${ROUTES.PURCHASE_REQUESTS}/:id`} element={<PurchaseRequestEditor />} />
                  <Route path={ROUTES.PURCHASE_ORDERS} element={<PurchaseOrdersList />} />
                  <Route path={`${ROUTES.PURCHASE_ORDERS}/new`} element={<PurchaseOrderEditor />} />
                  <Route path={`${ROUTES.PURCHASE_ORDERS}/:id`} element={<PurchaseOrderEditor />} />
                  <Route path={ROUTES.PURCHASE_SUPPLIERS} element={<SuppliersList />} />
                  <Route path={`${ROUTES.PURCHASE_SUPPLIERS}/new`} element={<SupplierForm />} />
                  <Route path={`${ROUTES.PURCHASE_SUPPLIERS}/:id/edit`} element={<SupplierForm />} />
                  <Route path={`${ROUTES.PURCHASE_ORDERS}/:id/recevoir`} element={<GoodsReceiptForm />} />
                  <Route path={ROUTES.PURCHASE_INVOICES} element={<PurchaseInvoicesList />} />
                  <Route path={ROUTES.PURCHASE_INVOICES_FROM_RECEIPTS} element={<InvoiceFromGoodsReceipts />} />
                  <Route path={`${ROUTES.PURCHASE_INVOICES}/new`} element={<PurchaseInvoiceForm />} />
                  <Route path={`${ROUTES.PURCHASE_INVOICES}/:id`} element={<PurchaseInvoiceForm />} />
                  <Route path={ROUTES.PURCHASE_RECEIPTS} element={<GoodsReceiptsList />} />
                  <Route path={ROUTES.PURCHASE_PAYMENTS} element={<PurchasePaymentsList />} />
                  <Route path={ROUTES.PURCHASE_TEJ_EXPORT} element={<TejExportPage />} />
                  <Route path={ROUTES.PURCHASE_PAYMENTS_REGULARIZATION_GLOBAL} element={<SupplierGlobalBalance />} />
                  <Route path={ROUTES.PURCHASE_PAYMENTS_REGULARIZATION} element={<SupplierPaymentRegularization />} />
                  <Route path={ROUTES.PURCHASE_RETURNS} element={<SupplierReturnsList />} />
                  <Route path={`${ROUTES.PURCHASE_RETURNS}/new`} element={<SupplierReturnForm />} />
                  <Route path={`${ROUTES.PURCHASE_RETURNS}/:id`} element={<SupplierReturnForm />} />
                  
                  {/* Stock */}
                  <Route path={ROUTES.STOCK_DASHBOARD} element={<StockDashboard />} />
                  <Route path={ROUTES.STOCK} element={<StockList />} />
                  <Route path={ROUTES.STOCK_ANALYTICS} element={<StockAnalytics />} />
                  
                  {/* Warehouses */}
                  <Route path={ROUTES.WAREHOUSES} element={<WarehousesList />} />
                  <Route path={`${ROUTES.WAREHOUSES}/new`} element={<WarehouseForm />} />
                  <Route path={`${ROUTES.WAREHOUSES}/:id/edit`} element={<WarehouseForm />} />
                  <Route path={ROUTES.STOCK_TRANSFER} element={<WarehouseTransfer />} />
                  
                  {/* Reports */}
                  <Route path={ROUTES.REPORTS} element={<Reports />} />
                  
                  {/* Analytics */}
                  <Route path={ROUTES.ANALYTICS} element={<Analytics />} />
                  
                  {/* Caisse (POS) */}
                  <Route path="/caisse" element={<CaisseDashboard />} />
                  <Route path="/caisse/pos" element={<POSInterface />} />
                  <Route path="/caisse/sessions/open" element={<SessionManager />} />
                  <Route path="/caisse/sessions/:id/close" element={<CloseSession />} />
                  <Route path="/caisse/sessions" element={<SessionHistory />} />
                  <Route path="/caisse/tickets" element={<TicketHistory />} />
                  <Route path="/caisse/points" element={<CaissePointsList />} />
                  <Route path="/caisse/mouvements" element={<CaisseMouvements />} />
                  <Route path="/caisse/pay-invoice" element={<PayInvoiceFromCaisse />} />
                  <Route path="/caisse/reports" element={<CaisseReports />} />
                  <Route path="/caisse/pos-full" element={<Navigate to="/pos" replace />} />
                  
                  {/* AI Chat */}
                  <Route path={ROUTES.CHAT_IA} element={<ChatIA />} />
                  
                  {/* Settings */}
                  <Route path={ROUTES.PROFILE} element={<Profile />} />
                  <Route path={ROUTES.SETTINGS} element={<Settings />} />
                  <Route
                    path={ROUTES.SETTINGS_CASHIERS}
                    element={
                      <ProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_GERANT']}>
                        <CashiersManagement />
                      </ProtectedRoute>
                    }
                  />

                  {/* Super Admin */}
                  <Route
                    path="/super-admin/users"
                    element={
                      <ProtectedRoute requiredRoles={['ROLE_SUPER_ADMIN']}>
                        <UserManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super-admin/entreprises"
                    element={
                      <ProtectedRoute requiredRoles={['ROLE_SUPER_ADMIN']}>
                        <EntrepriseManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/super-admin/roles"
                    element={
                      <ProtectedRoute requiredRoles={['ROLE_SUPER_ADMIN']}>
                        <RoleManagement />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Test page */}
                  <Route path="/test" element={<TestPage />} />
                </Route>

                {/* POS plein écran (sans Layout) */}
                <Route
                  element={
                    <ProtectedRoute>
                      <LayoutSimple />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/pos" element={<POSInterface />} />
                </Route>

                {/* Redirect root based on role */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <RootRedirect />
                    </ProtectedRoute>
                  }
                />

                {/* 404 */}
                <Route
                  path="*"
                  element={
                    <div className="flex items-center justify-center min-h-screen">
                      <div className="text-center">
                        <h1 className="text-6xl font-bold text-primary-500 mb-4">404</h1>
                        <p className="text-gray-600 mb-4">Page non trouvée</p>
                        <a href={ROUTES.DASHBOARD} className="text-primary-600 hover:text-primary-700">
                          Retour au tableau de bord
                        </a>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </Suspense>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
